/**
 * FOTOGRAVUR PREVIEW - Canvas-basierte Bildupload & S/W Konvertierung
 * - Bildupload mit FileReader
 * - S/W Konvertierung mit Floyd-Steinberg Dithering für Grautöne
 * - Optionale Hintergrundentfernung (TensorFlow.js BodyPix)
 * - Text mit 6 Schriftarten
 * - Live-Vorschau auf Canvas
 */

const FotogravurPreview = {
  // ========================================
  // KONFIGURATION
  // ========================================
  config: {
    // Base-Produktbild (Blumentopf ohne Gravur)
    baseImage: 'https://cdn.shopify.com/s/files/1/0696/9494/7595/files/vorschaubild.png?v=1761572868',

    // Canvas-Dimensionen
    canvasWidth: 800,
    canvasHeight: 800,

    // Max. Dateigröße (5 MB)
    maxFileSize: 5 * 1024 * 1024,

    // Backend-Upload URL
    uploadUrl: 'https://www.workloom.de/shopify-uploads/api/upload/',
    // KI-Auto-Optimierung (Nano Banana Flash + Dithering) fuer Lasergravur
    aiOptimizeUrl: 'https://www.workloom.de/shopify-uploads/api/ai-optimize-public/',
    aiAutoOptimize: true,  // beim Upload automatisch KI-optimieren

    // Schriftarten-Konfiguration
    fontConfig: {
      'Pixie Ring': {
        url: 'https://cdn.shopify.com/s/files/1/0696/9494/7595/files/Pixie_Ring_Regular.ttf?v=1761954704',
        name: 'Pixie Ring'
      },
      'Dreaming Outloud': {
        url: 'https://cdn.shopify.com/s/files/1/0696/9494/7595/files/dreaming-outloud-allcaps-regular.ttf?v=1761954704',
        name: 'Dreaming Outloud'
      },
      'Montserrat': {
        url: 'https://cdn.shopify.com/s/files/1/0696/9494/7595/files/Montserrat-VariableFont_wght.ttf?v=1761954705',
        name: 'Montserrat'
      },
      'Allura': {
        url: 'https://cdn.shopify.com/s/files/1/0696/9494/7595/files/Allura-Regular.ttf?v=1761954704',
        name: 'Allura'
      },
      'Bebas Neue': {
        url: 'https://cdn.shopify.com/s/files/1/0696/9494/7595/files/BebasNeue-Regular.ttf?v=1761954704',
        name: 'Bebas Neue'
      },
      'Amatic SC': {
        url: 'https://cdn.shopify.com/s/files/1/0696/9494/7595/files/AmaticSC-Regular.ttf?v=1761954704',
        name: 'Amatic SC'
      }
    },

    // Bild-Position auf Canvas
    imageConfig: {
      x: 400,        // Zentriert
      y: 445,        // Ein Tick weiter nach unten
      maxWidth: 350,
      maxHeight: 350
    },

    // Text-Position
    textConfig: {
      x: 400,
      y: 680,        // Weiter nach unten
      fontSize: 48,
      align: 'center',
      baseline: 'middle',
      color: '#4a4a4a'
    }
  },

  // Interner State
  canvas: null,
  ctx: null,
  images: {
    base: null,
    uploaded: null,
    processedUpload: null,  // S/W konvertiertes Bild (DEPRECATED - verwende Preview/Final/Engrave)
    processedUploadPreview: null,  // 400px Version für Thumbnail-Vorschau
    processedUploadFinal: null,    // 300px Version für finale Vorschau auf Topf
    processedUploadEngrave: null   // 2000px Version für Gravur/Upload
  },
  fontsLoaded: false,
  isRendering: false,
  currentFont: 'Pixie Ring',
  uploadedImage: null,
  bodyPixNet: null,  // TensorFlow BodyPix Modell
  bodyPixLoading: false,

  // Manuelles Masking
  maskCanvas: null,
  maskCtx: null,
  manualMask: null,  // Boolean array für manuelle Maske
  isDrawing: false,
  brushSize: 30,

  // Upload-Status
  uploadComplete: false,
  uploadedImageName: '',
  isUploading: false,  // Lock für parallele Uploads

  /**
   * Initialisierung
   */
  init() {
    this.canvas = document.getElementById('fg-preview-canvas');
    if (!this.canvas) {
      console.error('Canvas element not found');
      return;
    }

    this.ctx = this.canvas.getContext('2d');

    // Render-Aufloesung an die Mockup-Aufloesung (1080px) angepasst -> Mockup bleibt scharf (kein Hochskalieren)
    const RS = 1.35;
    this.canvas.width = this.config.canvasWidth * RS;
    this.canvas.height = this.config.canvasHeight * RS;
    this.ctx.scale(RS, RS);

    // WICHTIG: Image Smoothing deaktivieren für scharfe Dithering-Darstellung
    this.ctx.imageSmoothingEnabled = false;

    this.setupEventListeners();
    this.setupManualMask();
    this.loadFonts();
    this.loadBaseImage();
    this.setupCartIntegration();
    // Gespeicherten Gravur-Zustand wiederherstellen (spart Neugenerierung / Gemini-Kosten)
    setTimeout(() => { this.restoreState(); }, 600);
    // BodyPix wird nur bei Bedarf geladen (Lazy Loading)
  },

  /**
   * Ordered Dithering mit Bayer Matrix (4x4)
   * Regelmäßigeres Muster als Floyd-Steinberg, sieht sauberer aus
   * @param {ImageData} imageData - Die zu verarbeitenden Pixeldaten
   * @param {number} width - Bildbreite
   * @param {number} height - Bildhöhe
   * @param {number} threshold - S/W Schwellenwert (0-255)
   */
  applyOrderedDithering(imageData, width, height, threshold = 128) {
    const data = imageData.data;

    // Bayer Matrix 4x4 (normalisiert auf 0-1)
    const bayerMatrix = [
      [0/16,  8/16,  2/16, 10/16],
      [12/16, 4/16, 14/16,  6/16],
      [3/16, 11/16,  1/16,  9/16],
      [15/16, 7/16, 13/16,  5/16]
    ];

    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        const i = (y * width + x) * 4;

        // Grayscale-Wert (sollte bereits grayscale sein)
        const grayscale = data[i];

        // Bayer-Threshold für diese Position
        const bayerValue = bayerMatrix[y % 4][x % 4];
        const adjustedThreshold = threshold + (bayerValue - 0.5) * 128;

        // S/W Entscheidung
        const bw = grayscale > adjustedThreshold ? 255 : 0;

        data[i] = bw;
        data[i + 1] = bw;
        data[i + 2] = bw;
      }
    }
  },

  /**
   * Floyd-Steinberg Dithering für Graustufen-Simulation durch S/W-Pixel
   * Verteilt Quantisierungsfehler auf benachbarte Pixel für bessere Graustufen-Darstellung
   * Grautöne werden durch clevere Anordnung von schwarzen/weißen Pixeln simuliert
   * @param {ImageData} imageData - Die zu verarbeitenden Pixeldaten
   * @param {number} width - Bildbreite
   * @param {number} height - Bildhöhe
   * @param {number} threshold - Schwellenwert für S/W-Konvertierung (0-255)
   */
  applyFloydSteinbergDithering(imageData, width, height, threshold = 128) {
    const data = imageData.data;

    // Arbeits-Array für Graustufenwerte (um Fehler zu akkumulieren)
    const grayscale = new Float32Array(width * height);

    // Erst alle Pixel in Graustufen konvertieren
    for (let i = 0; i < data.length; i += 4) {
      const r = data[i];
      const g = data[i + 1];
      const b = data[i + 2];
      grayscale[i / 4] = 0.299 * r + 0.587 * g + 0.114 * b;
    }

    // Floyd-Steinberg Dithering anwenden
    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        const idx = y * width + x;
        const oldPixel = grayscale[idx];

        // Quantisierung: Auf 0 oder 255 runden (mit anpassbarem Threshold)
        const newPixel = oldPixel < threshold ? 0 : 255;
        grayscale[idx] = newPixel;

        // Fehler berechnen (z.B. 127 - 255 = -128)
        const error = oldPixel - newPixel;

        // Fehler auf benachbarte Pixel verteilen (Floyd-Steinberg Matrix)
        // Dadurch entsteht ein Muster aus schwarzen/weißen Pixeln, das Grautöne simuliert
        if (x + 1 < width) {
          grayscale[idx + 1] += error * 7 / 16;  // Rechts: 43.75%
        }
        if (y + 1 < height) {
          if (x > 0) {
            grayscale[idx + width - 1] += error * 3 / 16;  // Unten-links: 18.75%
          }
          grayscale[idx + width] += error * 5 / 16;  // Unten: 31.25%
          if (x + 1 < width) {
            grayscale[idx + width + 1] += error * 1 / 16;  // Unten-rechts: 6.25%
          }
        }
      }
    }

    // Zurück in ImageData schreiben
    // Nach dem Dithering sind alle Werte bereits 0 oder 255
    for (let i = 0; i < grayscale.length; i++) {
      const value = grayscale[i];
      data[i * 4] = value;
      data[i * 4 + 1] = value;
      data[i * 4 + 2] = value;
      // Alpha bleibt unverändert
    }

    return imageData;
  },

  /**
   * TensorFlow.js BodyPix laden
   */
  async loadBodyPix() {
    if (this.bodyPixLoading || this.bodyPixNet) return;
    this.bodyPixLoading = true;

    try {
      // TensorFlow.js laden (falls noch nicht geladen)
      if (!window.tf) {
        await this.loadScript('https://cdn.jsdelivr.net/npm/@tensorflow/tfjs@4.11.0/dist/tf.min.js');
      }

      // BodyPix laden
      if (!window.bodyPix) {
        await this.loadScript('https://cdn.jsdelivr.net/npm/@tensorflow-models/body-pix@2.2.0/dist/body-pix.min.js');
      }

      // Modell laden (MobileNetV1 - schneller, kleineres Modell)
      this.bodyPixNet = await bodyPix.load({
        architecture: 'MobileNetV1',
        outputStride: 16,
        multiplier: 0.75,
        quantBytes: 2
      });

      this.bodyPixLoading = false;
    } catch (error) {
      console.error('Error loading BodyPix:', error);
      this.bodyPixLoading = false;
    }
  },

  /**
   * Script dynamisch laden
   */
  loadScript(src) {
    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = src;
      script.onload = resolve;
      script.onerror = reject;
      document.head.appendChild(script);
    });
  },

  /**
   * Event Listeners einrichten
   */
  setupEventListeners() {
    // Bildupload
    const imageUpload = document.getElementById('fg-image-upload');
    if (imageUpload) {
      imageUpload.addEventListener('change', (e) => this.handleImageUpload(e));
    }

    // Vorschau-Button
    const previewBtn = document.getElementById('fg-preview-btn');
    if (previewBtn) {
      previewBtn.addEventListener('click', () => this.openPreview());
    }

    // Modal schließen
    const closeBtn = document.querySelector('.fg-preview-close');
    if (closeBtn) {
      closeBtn.addEventListener('click', () => this.closePreview());
    }

    // In den Warenkorb Button (im Preview Modal)
    const addToCartBtn = document.getElementById('fg-add-to-cart-btn');
    if (addToCartBtn) {
      addToCartBtn.addEventListener('click', () => this.addToCartFromPreview());
    }

    // Modal-Hintergrund
    const modal = document.getElementById('fg-preview-modal');
    if (modal) {
      modal.addEventListener('click', (e) => {
        if (e.target.id === 'fg-preview-modal') {
          this.closePreview();
        }
      });
    }

    // ESC-Taste
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        this.closePreview();
        this.closeFontModal();
      }
    });

    // Textfeld - Schriftartenauswahl einblenden bei Eingabe
    const textField = document.getElementById('fg-text');
    if (textField) {
      textField.addEventListener('input', () => {
        this.updateFontFieldVisibility();
        // Wunschtext live in der (Inline-)Vorschau aktualisieren
        clearTimeout(this._textPreviewTimer);
        this._textPreviewTimer = setTimeout(() => {
          if (this.images.processedUpload || this.uploadedImage) {
            this.renderPreview(this.getFormData());
          }
        }, 250);
      });
    }

    // Schriftart-Selector Button
    const fontSelectorBtn = document.getElementById('fg-font-selector-btn');
    if (fontSelectorBtn) {
      fontSelectorBtn.addEventListener('click', () => this.openFontModal());
    }

    // Font Modal schließen
    const fontCloseBtn = document.querySelector('.fg-font-close');
    if (fontCloseBtn) {
      fontCloseBtn.addEventListener('click', () => this.closeFontModal());
    }

    // Modal-Hintergrund klicken
    const fontModal = document.getElementById('fg-font-modal');
    if (fontModal) {
      fontModal.addEventListener('click', (e) => {
        if (e.target.id === 'fg-font-modal') {
          this.closeFontModal();
        }
      });
    }

    // Schriftart-Auswahl
    const fontRadios = document.querySelectorAll('input[name="fg-font-choice"]');
    fontRadios.forEach(radio => {
      radio.addEventListener('change', (e) => {
        const hiddenFont = document.getElementById('fg-font');
        if (hiddenFont) {
          hiddenFont.value = e.target.value;
          this.currentFont = e.target.value;
        }

        // Button-Text aktualisieren
        const selectedFontName = document.getElementById('fg-selected-font-name');
        if (selectedFontName) {
          selectedFontName.textContent = e.target.value;
        }

        // Modal schließen nach Auswahl
        this.closeFontModal();
      });
    });

    // Hintergrund entfernen Checkbox
    const bgCheckbox = document.getElementById('fg-remove-background');
    if (bgCheckbox) {
      bgCheckbox.addEventListener('change', async () => {
        // Manuellen Masken-Button ein-/ausblenden
        this.updateManualMaskButtonVisibility();

        try {
          // BodyPix laden wenn aktiviert (Lazy Loading) – kann etwas dauern
          if (bgCheckbox.checked && !this.bodyPixNet && !this.bodyPixLoading) {
            this.showPreviewLoading(true, 'Hintergrund-Erkennung wird geladen …');
            await this.loadBodyPix();
          }

          if (this.uploadedImage) {
            this.showPreviewLoading(true, bgCheckbox.checked ? 'Hintergrund wird entfernt …' : 'Wird verarbeitet …');
            await this.reprocessImage();

            // Wenn Preview Modal offen ist, Vorschau aktualisieren
            const modal = document.getElementById('fg-preview-modal');
            if (modal && modal.style.display === 'flex') {
              await this.renderPreview(this.getFormData());
            }
          }
        } finally {
          this.showPreviewLoading(false);
        }
      });
    }

    // Bildanpassungs-Slider
    this.setupAdjustmentSliders();

    // Toggle-Button für Slider
    this.setupSliderToggle();

    // Zoom/Vollbild auf der Inline-Vorschau
    this.setupZoom();
  },

  // Vollbild-Zoom der Inline-Vorschau (Button oben rechts auf dem Vorschaubild)
  setupZoom() {
    const btn = document.getElementById('fg-zoom-btn');
    if (!btn) return;
    btn.addEventListener('click', (e) => {
      e.preventDefault();
      // Im Zoom das ECHTE 1-Bit-Gravurbild zeigen (scharf, mit Dithering = wie Workloom),
      // nicht das verkleinerte/geglättete Topf-Mockup.
      const eng = this.images.processedUploadEngrave || this.images.processedUpload;
      let src = (eng && eng.src) ? eng.src : '';
      if (!src) { const im = document.getElementById('fg-inline-preview-img'); src = im ? im.src : ''; }
      if (src) this.openZoom(src);
    });
  },

  openZoom(src) {
    let ov = document.getElementById('fg-zoom-overlay');
    if (!ov) {
      ov = document.createElement('div');
      ov.id = 'fg-zoom-overlay';
      ov.style.cssText = 'position:fixed;inset:0;z-index:100000;display:none;align-items:center;' +
        'justify-content:center;background:rgba(0,0,0,0.9);padding:16px;cursor:zoom-out;';
      ov.innerHTML =
        '<img id="fg-zoom-img" alt="Gravur-Vorschau Vollbild" style="max-width:100%;max-height:100%;border-radius:6px;cursor:default;background:#faf8f4;">' +
        '<button type="button" id="fg-zoom-close" aria-label="Schließen" style="position:absolute;top:14px;right:18px;' +
        'background:rgba(255,255,255,0.18);color:#fff;border:none;border-radius:50%;width:42px;height:42px;' +
        'font-size:26px;line-height:1;cursor:pointer;">×</button>';
      document.body.appendChild(ov);
      const close = () => { ov.style.display = 'none'; };
      ov.addEventListener('click', (e) => {
        if (e.target.id !== 'fg-zoom-img') close();
      });
    }
    const zi = ov.querySelector('#fg-zoom-img');
    if (zi) zi.src = src;
    ov.style.display = 'flex';
  },

  /**
   * Bildanpassungs-Slider einrichten
   */
  setupAdjustmentSliders() {
    const sliders = ['fg-brightness', 'fg-contrast', 'fg-threshold'];

    sliders.forEach(sliderId => {
      const slider = document.getElementById(sliderId);
      const valueDisplay = document.getElementById(sliderId + '-value');

      if (slider && valueDisplay) {
        // Wert-Anzeige aktualisieren
        slider.addEventListener('input', (e) => {
          valueDisplay.textContent = e.target.value;
        });

        // Bild neu verarbeiten beim Loslassen (bessere Performance)
        slider.addEventListener('change', async () => {
          if (this.uploadedImage) {
            await this.reprocessImage();

            // Wenn Preview Modal offen ist, Vorschau aktualisieren
            const modal = document.getElementById('fg-preview-modal');
            if (modal && modal.style.display === 'flex') {
              await this.renderPreview(this.getFormData());
            }
          }
        });
      }
    });
  },

  /**
   * Toggle-Button für Slider einrichten
   */
  setupSliderToggle() {
    const toggleBtn = document.getElementById('fg-toggle-sliders');
    const slidersContainer = document.getElementById('fg-sliders-container');

    if (toggleBtn && slidersContainer) {
      toggleBtn.addEventListener('click', () => {
        const isVisible = slidersContainer.style.display !== 'none';
        slidersContainer.style.display = isVisible ? 'none' : 'block';
      });
    }

    // Reset Button
    const resetBtn = document.getElementById('fg-reset-adjustments');
    if (resetBtn) {
      resetBtn.addEventListener('click', async () => {
        document.getElementById('fg-brightness').value = 0;
        document.getElementById('fg-contrast').value = 0;
        document.getElementById('fg-threshold').value = 127;
        document.getElementById('fg-brightness-value').textContent = '0';
        document.getElementById('fg-contrast-value').textContent = '0';
        document.getElementById('fg-threshold-value').textContent = '127';

        if (this.uploadedImage) {
          await this.reprocessImage();

          // Wenn Preview Modal offen ist, Vorschau aktualisieren
          const modal = document.getElementById('fg-preview-modal');
          if (modal && modal.style.display === 'flex') {
            await this.renderPreview(this.getFormData());
          }
        }
      });
    }
  },

  /**
   * Berechnet automatische Bildanpassungen (Auto-Kontrast & Auto-Helligkeit)
   * OPTIMIERT: Konservativere Werte um Dithering-Artefakte zu vermeiden
   */
  async calculateAutoAdjustments(img) {
    return new Promise((resolve) => {
      const tempCanvas = document.createElement('canvas');
      const tempCtx = tempCanvas.getContext('2d');

      // Höhere Sampling-Auflösung für bessere Analyse
      const sampleSize = Math.min(Math.max(img.width, img.height), 1000);
      const scale = sampleSize / Math.max(img.width, img.height);
      tempCanvas.width = Math.round(img.width * scale);
      tempCanvas.height = Math.round(img.height * scale);

      // Bild zeichnen
      tempCtx.drawImage(img, 0, 0, tempCanvas.width, tempCanvas.height);

      // Pixeldaten analysieren
      const imageData = tempCtx.getImageData(0, 0, tempCanvas.width, tempCanvas.height);
      const data = imageData.data;

      let min = 255;
      let max = 0;
      let sum = 0;
      let count = 0;

      // Histogramm und Statistiken berechnen
      for (let i = 0; i < data.length; i += 4) {
        // Grayscale berechnen
        const gray = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];

        min = Math.min(min, gray);
        max = Math.max(max, gray);
        sum += gray;
        count++;
      }

      const mean = sum / count;
      const currentRange = max - min;

      // AGGRESSIVE Kontrast-Anpassung für optimale Sichtbarkeit
      let contrastAdjustment = 0;
      if (currentRange < 180) {
        // Kontrast deutlich erhöhen bei flachen Bildern
        contrastAdjustment = Math.min(60, Math.round((220 - currentRange) / 2));
      } else if (currentRange < 220) {
        // Moderate Kontrast-Erhöhung
        contrastAdjustment = Math.min(40, Math.round((220 - currentRange) / 3));
      }

      // AGGRESSIVE Helligkeits-Anpassung für optimale Sichtbarkeit
      let brightnessAdjustment = 0;
      const targetMean = 127;

      if (mean < 100) {
        // Dunkle Bilder deutlich aufhellen
        brightnessAdjustment = Math.min(70, Math.round((targetMean - mean) / 1.5));
      } else if (mean > 155) {
        // Helle Bilder deutlich abdunkeln
        brightnessAdjustment = Math.max(-70, Math.round((targetMean - mean) / 1.5));
      } else if (mean < 120) {
        // Leicht dunkle Bilder aufhellen
        brightnessAdjustment = Math.min(40, Math.round((targetMean - mean) / 2));
      } else if (mean > 135) {
        // Leicht helle Bilder abdunkeln
        brightnessAdjustment = Math.max(-40, Math.round((targetMean - mean) / 2));
      }

      // THRESHOLD: Dynamisch anpassen für beste Sichtbarkeit
      let threshold = 127;
      const adjustedMean = mean + brightnessAdjustment;

      if (adjustedMean < 110) {
        threshold = 105; // Niedriger für dunkle Bilder
      } else if (adjustedMean > 145) {
        threshold = 150; // Höher für helle Bilder
      } else if (adjustedMean < 120) {
        threshold = 115; // Leicht niedriger
      } else if (adjustedMean > 135) {
        threshold = 140; // Leicht höher
      }

      resolve({
        brightness: brightnessAdjustment,
        contrast: contrastAdjustment,
        threshold: threshold
      });
    });
  },

  /**
   * Bild neu verarbeiten mit aktuellen Slider-Werten
   */
  async reprocessImage() {
    if (!this.uploadedImage) return;

    // Wenn manuelle Maske vorhanden, diese verwenden
    if (this.manualMask) {
      await this.processWithManualMask();
    } else {
      await this.processUploadedImage(this.uploadedImage);
    }

    // Vorschau-Thumbnail aktualisieren und auf Laden warten
    const thumbnail = document.getElementById('fg-image-preview-thumbnail');
    if (thumbnail && this.images.processedUpload) {
      await new Promise((resolve) => {
        thumbnail.onload = resolve;
        thumbnail.src = this.images.processedUpload.src;
      }).catch(() => {
        // Fehler ignorieren, Thumbnail ist optional
      });
    }
  },

  /**
   * Skaliert Bild auf maximale Größe für bessere Performance (DataURL Version)
   */
  async resizeImageDataUrl(dataUrl, fileType, maxSize = 1500) {
    return new Promise((resolve, reject) => {
      const img = new Image();

      img.onload = () => {
        let width = img.width;
        let height = img.height;

        // Nur skalieren wenn Bild größer als maxSize
        if (width <= maxSize && height <= maxSize) {
          resolve(dataUrl);
          return;
        }

        // Skalierung berechnen
        const originalWidth = width;
        const originalHeight = height;

        if (width > height) {
          if (width > maxSize) {
            height = Math.round((height * maxSize) / width);
            width = maxSize;
          }
        } else {
          if (height > maxSize) {
            width = Math.round((width * maxSize) / height);
            height = maxSize;
          }
        }

        // Canvas erstellen und skalieren
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);

        // Zurück zu DataURL konvertieren
        const resizedDataUrl = canvas.toDataURL(fileType || 'image/jpeg', 0.92);

        const originalSize = Math.round((dataUrl.length * 0.75) / 1024); // Base64 ~ 4/3 größer
        const newSize = Math.round((resizedDataUrl.length * 0.75) / 1024);

        resolve(resizedDataUrl);
      };

      img.onerror = () => {
        reject(new Error('Bild konnte nicht geladen werden'));
      };

      // DataURL direkt laden
      img.src = dataUrl;
    });
  },

  /**
   * Original-Bild SOFORT nach Auswahl zu workloom.de hochladen
   * Dies umgeht das File-Objekt-Problem auf mobilen Geräten
   */
  async uploadOriginalImageImmediately(fileDataUrl, fileName, fileType) {

    // Unique ID generieren
    const uniqueId = 'fotogravur_' + Date.now() + '_' + Math.random().toString(36).substring(7);

    try {
      // WICHTIG: Bild auf max 2000px skalieren (Gravur-Auflösung: 100mm / 0,05mm = 2000px)
      const resizedDataUrl = await this.resizeImageDataUrl(fileDataUrl, fileType, 2000);

      const originalImageData = resizedDataUrl;

      const payload = {
        unique_id: uniqueId,
        original_image_data: originalImageData,
        original_filename: fileName || 'unknown.jpg'
      };

      const response = await fetch(this.config.uploadUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload)
      });

      const result = await response.json();

      if (result.success && result.original_image_url) {
        return {
          uniqueId: result.unique_id,
          originalImageUrl: result.original_image_url
        };
      } else {
        throw new Error(result.error || 'Upload fehlgeschlagen');
      }
    } catch (error) {
      throw error;
    }
  },

  /**
   * Bildupload verarbeiten
   */
  async handleImageUpload(event) {

    // Lock prüfen - verhindert parallele Uploads
    if (this.isUploading) {
      return;
    }

    // KRITISCH: File-Referenz SOFORT speichern, bevor irgendetwas Async passiert!
    const inputElement = event.target;
    const file = inputElement?.files ? inputElement.files[0] : null;

    if (!file) {
      return;
    }

    // Ladeindikator anzeigen
    this.showProcessingSpinner(true);
    this._aiSaved = false;
    this._publicFigureBlocked = false;
    this.hidePublicFigureNotice();
    // Sichtbare Ladeanzeige auf der Produktseite (KI-Optimierung laeuft gleich)
    if (this.config.aiAutoOptimize) this.showInlineLoading(true, 'Wir optimieren dein Bild für die Gravur …');

    // ALLERWICHTIGST: FileReader SOFORT SYNCHRON starten, BEVOR wir irgendwas anderes machen!
    // NICHT MAL Properties vom File lesen! Die könnten es invalidieren!
    const reader = new FileReader();

    // Promise für FileReader - Setup MUSS vor readAsDataURL sein!
    const fileDataPromise = new Promise((resolve, reject) => {
      reader.onload = (e) => {
        resolve({
          dataUrl: e.target.result,
          fileName: file.name,
          fileSize: file.size,
          fileType: file.type
        });
      };
      reader.onerror = (error) => {
        reject(new Error('FileReader fehlgeschlagen'));
      };
    });

    // JETZT starten - im gleichen Call Stack, BEVOR irgendwas anderes passiert!
    reader.readAsDataURL(file);

    // Lock setzen
    this.isUploading = true;

    try {
      // Warte auf FileReader (der läuft schon!)
      const fileData = await fileDataPromise;

      // Jetzt können wir Properties sicher lesen (aus dem Promise-Result)

      // Dateigröße prüfen
      if (fileData.fileSize > this.config.maxFileSize) {
        this.showProcessingSpinner(false);
        alert('Die Datei ist zu groß. Maximal 5 MB erlaubt.');
        inputElement.value = '';
        return;
      }

      // Dateityp prüfen
      if (!fileData.fileType.match(/image\/(jpeg|jpg|png)/)) {
        this.showProcessingSpinner(false);
        alert('Bitte nur JPG oder PNG Dateien hochladen.');
        inputElement.value = '';
        return;
      }

      // Upload-Status zurücksetzen für neues Bild
      this.uploadComplete = false;
      this.uploadedImageName = fileData.fileName;
      this.manualMask = null;  // Manuelle Maske zurücksetzen bei neuem Bild

      // NEUER ANSATZ: Bild SOFORT zu workloom.de hochladen und von dort laden
      const uploadResult = await this.uploadOriginalImageImmediately(fileData.dataUrl, fileData.fileName, fileData.fileType);

      // Speichere unique_id für späteren S/W-Bild Upload
      this.currentUniqueId = uploadResult.uniqueId;
      // ROBUST: Bild-ID SOFORT ins Warenkorb-Feld schreiben, sobald die ID bekannt ist
      // (nicht erst beim Submit) -> hängt zuverlässig schon beim ersten Hinzufügen am Line-Item.
      // Ebenso das Kennzeichen: syncFieldsToCart laeuft nach dem Upload nicht
      // automatisch erneut, dadurch blieb in der Bestellung 'Nein' stehen,
      // obwohl direkt daneben eine gueltige Bild-ID stand.
      if (this.currentUniqueId) {
        this._setHidden('fg-hidden-image-data', this.currentUniqueId);
        this._setHidden('fg-hidden-image-uploaded', 'Ja');
      }

      // Erstelle Image-Element und lade von workloom.de URL
      const img = new Image();
      img.crossOrigin = 'anonymous';  // CORS für Backend-Bilder

      img.onload = async () => {
        this.uploadedImage = img;

        // Automatische Bildanpassung durchführen
        try {
          const adjustments = await this.calculateAutoAdjustments(img);

          // Slider automatisch aktualisieren
          document.getElementById('fg-brightness').value = adjustments.brightness;
          document.getElementById('fg-contrast').value = adjustments.contrast;
          document.getElementById('fg-threshold').value = adjustments.threshold;
          document.getElementById('fg-brightness-value').textContent = adjustments.brightness;
          document.getElementById('fg-contrast-value').textContent = adjustments.contrast;
          document.getElementById('fg-threshold-value').textContent = adjustments.threshold;
        } catch (error) {
          console.error('Auto-Adjust Fehler:', error);
          // Bei Fehler: Standard-Werte beibehalten
        }

        this._originalImg = img;  // Referenz fuer clientseitige Freistellung, falls KI ablehnt
        await this.processUploadedImage(img);

        await this.showImagePreview(null, img);

        this.enablePreviewButton();

        // NEU: automatische KI-Optimierung fuer die Lasergravur (Nano Banana Flash + Dithering)
        await this.aiAutoOptimize();

        // Bearbeitetes Gravur-Bild speichern – ABER nur, wenn die KI es nicht schon serverseitig
        // gespeichert hat (sonst wuerde die Graustufen-Vorschau das 1-Bit-Gravurbild ueberschreiben).
        try {
          const engrave = this.images.processedUploadEngrave || this.images.processedUpload;
          if (this._publicFigureBlocked) {
            console.warn('⚠ Bild als Person des öffentlichen Lebens erkannt – kein Upload/keine Bestellung');
          } else if (this._aiSaved) {
            console.log('✓ Gravur-Bild bereits per KI gespeichert – kein erneuter Upload');
          } else if (this.currentUniqueId && engrave && engrave.src) {
            await this.uploadImageToServer(engrave.src, this.getFormData());
            console.log('✓ Bearbeitetes Gravur-Bild direkt nach Upload gespeichert');
          } else {
            console.warn('Engrave-Upload übersprungen:', { uid: this.currentUniqueId, hasEngrave: !!engrave });
          }
        } catch (e) {
          console.warn('Engrave-Upload (direkt) fehlgeschlagen:', e);
        }

      };

      img.onerror = (errorEvent) => {
        this.showProcessingSpinner(false);
        alert('Fehler beim Laden des Bildes vom Server. Bitte versuche es erneut.');
      };

      // Lade Bild von workloom.de
      img.src = uploadResult.originalImageUrl;

    } catch (error) {
      this.showProcessingSpinner(false);
      alert('Fehler beim Hochladen des Bildes. Bitte versuche es erneut.');
    } finally {
      // Lock freigeben
      this.isUploading = false;
    }
  },

  /**
   * Hochgeladenes Bild verarbeiten (S/W + optional KI Person Segmentation)
   */
  async processUploadedImage(img) {
    const removeBackground = document.getElementById('fg-remove-background')?.checked || false;

    // Spinner anzeigen
    this.showProcessingSpinner(true);

    try {
      // BodyPix laden falls benötigt (Lazy Loading)
      if (removeBackground && !this.bodyPixNet && !this.bodyPixLoading) {
        await this.loadBodyPix();
      }

      if (removeBackground && this.bodyPixNet) {
        await this.processWithPersonSegmentation(img);
      } else {
        await this.processSimpleBlackWhite(img);
      }
      const hasProcessed = !!this.images.processedUpload;
      if (this.images.processedUpload) {
      }
    } catch (error) {
      // Fallback auf einfache S/W Konvertierung
      await this.processSimpleBlackWhite(img);
    } finally {
      // Spinner verstecken
      this.showProcessingSpinner(false);
    }
  },

  /**
   * KI Person Segmentation mit BodyPix
   * Erstellt DREI Versionen:
   * - Preview (400px) für Thumbnail-Anzeige
   * - Final (300px) für finale Vorschau auf Topf
   * - Engrave (2000px) für Gravur/Upload
   */
  async processWithPersonSegmentation(img) {
    const PREVIEW_SIZE = 400;  // Thumbnail
    const FINAL_SIZE = 1200;   // Finale Vorschau auf Topf - hochauflösend für bessere Qualität beim Skalieren
    const ENGRAVE_SIZE = 2000; // Upload/Gravur

    // Slider-Werte holen
    const brightness = parseInt(document.getElementById('fg-brightness')?.value || 0);
    const contrast = parseInt(document.getElementById('fg-contrast')?.value || 0);
    const threshold = parseInt(document.getElementById('fg-threshold')?.value || 127);

    // VERSION 1: Preview (400px) - OHNE Dithering (Grayscale)
    const previewImg = await this.scaleToSize(img, PREVIEW_SIZE);
    const previewSegmentation = await this.bodyPixNet.segmentPerson(previewImg, {
      flipHorizontal: false,
      internalResolution: 'medium',
      segmentationThreshold: 0.5
    });
    const previewDataUrl = await this.processToDitheredCanvas(
      previewImg, brightness, contrast, threshold, previewSegmentation.data, false
    );

    // VERSION 2: Final (1200px) - OHNE Dithering (Grayscale)
    const finalImg = await this.scaleToSize(img, FINAL_SIZE);
    const finalSegmentation = await this.bodyPixNet.segmentPerson(finalImg, {
      flipHorizontal: false,
      internalResolution: 'medium',
      segmentationThreshold: 0.5
    });
    const finalDataUrl = await this.processToDitheredCanvas(
      finalImg, brightness, contrast, threshold, finalSegmentation.data, false
    );

    // VERSION 3: Engrave (2000px) - MIT Dithering (für Gravur)
    const engraveImg = await this.scaleToSize(img, ENGRAVE_SIZE);
    const engraveSegmentation = await this.bodyPixNet.segmentPerson(engraveImg, {
      flipHorizontal: false,
      internalResolution: 'medium',
      segmentationThreshold: 0.5
    });
    const engraveDataUrl = await this.processToDitheredCanvas(
      engraveImg, brightness, contrast, threshold, engraveSegmentation.data, true
    );

    // Preview-Image erstellen und laden
    this.images.processedUploadPreview = new Image();
    await new Promise((resolve) => {
      this.images.processedUploadPreview.onload = () => resolve();
      this.images.processedUploadPreview.onerror = () => resolve();
      this.images.processedUploadPreview.src = previewDataUrl;
    });

    // Final-Image erstellen und laden
    this.images.processedUploadFinal = new Image();
    await new Promise((resolve) => {
      this.images.processedUploadFinal.onload = () => resolve();
      this.images.processedUploadFinal.onerror = () => resolve();
      this.images.processedUploadFinal.src = finalDataUrl;
    });

    // Engrave-Image erstellen und laden
    this.images.processedUploadEngrave = new Image();
    await new Promise((resolve) => {
      this.images.processedUploadEngrave.onload = () => resolve();
      this.images.processedUploadEngrave.onerror = () => resolve();
      this.images.processedUploadEngrave.src = engraveDataUrl;
    });

    // Legacy: Für Abwärtskompatibilität
    this.images.processedUpload = this.images.processedUploadPreview;
    // Bearbeitetes Gravur-Bild robust automatisch speichern (debounced) – unabhängig vom Submit-Hook
    this._scheduleEngraveUpload();
  },

  /**
   * Skaliert Bild auf gegebene Zielgröße
   * @param {Image} img - Quellbild
   * @param {number} maxSize - Maximale Breite/Höhe
   * @returns {Image} Skaliertes Bild
   */
  scaleToSize(img, maxSize) {
    let width = img.width;
    let height = img.height;

    // Nur skalieren wenn Bild größer als Zielgröße
    if (width <= maxSize && height <= maxSize) {
      return img;
    }

    // Skalierung berechnen (Seitenverhältnis beibehalten)
    if (width > height) {
      if (width > maxSize) {
        height = Math.round((height * maxSize) / width);
        width = maxSize;
      }
    } else {
      if (height > maxSize) {
        width = Math.round((width * maxSize) / height);
        height = maxSize;
      }
    }

    // Canvas für Skalierung
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');

    // Hochqualitatives Skalieren
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(img, 0, 0, width, height);

    // Als neues Image zurückgeben
    const scaledImg = new Image();
    scaledImg.src = canvas.toDataURL();

    return new Promise((resolve) => {
      scaledImg.onload = () => resolve(scaledImg);
      scaledImg.onerror = () => resolve(img); // Fallback auf Original
    });
  },

  /**
   * Legacy-Funktion: Skaliert Bild auf Gravur-Auflösung (2000px)
   */
  scaleToEngraverResolution(img) {
    return this.scaleToSize(img, 2000); // 100mm / 0,05mm = 2000px
  },

  /**
   * Verarbeitet ein Bild zu einem S/W Canvas
   * @param {Image} img - Quellbild
   * @param {number} brightness - Helligkeit (-100 bis 100)
   * @param {number} contrast - Kontrast (-100 bis 100)
   * @param {number} threshold - S/W Schwellenwert (0-255)
   * @param {Array|null} personMask - Optional: Person-Maske für Hintergrundentfernung
   * @param {boolean} applyDithering - Dithering anwenden? (true für Gravur, false für Vorschau)
   * @returns {string} Data URL des verarbeiteten Canvas
   */
  async processToDitheredCanvas(img, brightness, contrast, threshold, personMask = null, applyDithering = true) {
    const tempCanvas = document.createElement('canvas');
    const tempCtx = tempCanvas.getContext('2d');

    tempCanvas.width = img.width;
    tempCanvas.height = img.height;

    // Bild zeichnen
    tempCtx.drawImage(img, 0, 0);

    // Pixeldaten holen
    const imageData = tempCtx.getImageData(0, 0, tempCanvas.width, tempCanvas.height);
    const data = imageData.data;

    // Kontrast-Faktor berechnen
    const contrastFactor = (259 * (contrast + 255)) / (255 * (259 - contrast));

    // Schritt 1: Helligkeit/Kontrast anpassen und zu Graustufen konvertieren
    for (let i = 0; i < data.length; i += 4) {
      let r = data[i];
      let g = data[i + 1];
      let b = data[i + 2];

      // Helligkeit anpassen
      r = Math.max(0, Math.min(255, r + brightness));
      g = Math.max(0, Math.min(255, g + brightness));
      b = Math.max(0, Math.min(255, b + brightness));

      // Kontrast anpassen
      r = Math.max(0, Math.min(255, contrastFactor * (r - 128) + 128));
      g = Math.max(0, Math.min(255, contrastFactor * (g - 128) + 128));
      b = Math.max(0, Math.min(255, contrastFactor * (b - 128) + 128));

      // Grayscale berechnen
      const grayscale = 0.299 * r + 0.587 * g + 0.114 * b;

      data[i] = grayscale;
      data[i + 1] = grayscale;
      data[i + 2] = grayscale;
    }

    // Grayscale-Version für später speichern (für Vorschau-Mix)
    const grayscaleData = new Uint8ClampedArray(data);

    // Schritt 2: Dithering anwenden
    if (applyDithering) {
      // Für Gravur: Volles Floyd-Steinberg Dithering
      this.applyFloydSteinbergDithering(imageData, tempCanvas.width, tempCanvas.height, threshold);
    } else {
      // Für Vorschau: Reduziertes Dithering
      this.applyFloydSteinbergDithering(imageData, tempCanvas.width, tempCanvas.height, threshold);

      // Mobile Detection für bessere Qualität auf kleinen Bildschirmen
      const isMobile = window.innerWidth <= 768;

      // Desktop: 70% Grayscale + 30% Dithered
      // Mobile: 85% Grayscale + 15% Dithered (bessere Erkennbarkeit)
      const grayscaleRatio = isMobile ? 0.85 : 0.7;
      const ditheredRatio = isMobile ? 0.15 : 0.3;

      // Mix für bessere Erkennbarkeit
      for (let i = 0; i < data.length; i += 4) {
        const gray = grayscaleData[i];
        const dithered = data[i];
        const mixed = Math.round(gray * grayscaleRatio + dithered * ditheredRatio);

        data[i] = mixed;
        data[i + 1] = mixed;
        data[i + 2] = mixed;
      }
    }

    // Schritt 3: Transparenz anwenden
    // Optional: Person-Maske anwenden
    if (personMask) {
      for (let i = 0; i < personMask.length; i++) {
        const pixelIndex = i * 4;
        const isPerson = personMask[i];

        if (isPerson) {
          if (applyDithering) {
            // Mit Dithering: Weiße Pixel → Transparent (nur schwarze Linien bleiben)
            if (data[pixelIndex] > 127) {
              data[pixelIndex + 3] = 0;
            }
          } else {
            // Ohne Dithering: Graustufen anzeigen, hellere Bereiche transparent
            const gray = data[pixelIndex];
            if (gray > 200) {
              data[pixelIndex + 3] = 0;  // Sehr helle Bereiche transparent
            } else {
              // Dunklere Bereiche bleiben sichtbar mit voller Deckkraft
              data[pixelIndex + 3] = 255;
            }
          }
        } else {
          // Hintergrund: Transparent
          data[pixelIndex + 3] = 0;
        }
      }
    } else {
      if (applyDithering) {
        // Mit Dithering: Nur weiße Pixel transparent machen
        for (let i = 0; i < data.length; i += 4) {
          if (data[i] > 127) {
            data[i + 3] = 0;
          }
        }
      } else {
        // Ohne Dithering: Graustufen mit Alpha-Mapping
        for (let i = 0; i < data.length; i += 4) {
          const gray = data[i];
          if (gray > 200) {
            data[i + 3] = 0;  // Sehr helle Bereiche transparent
          } else {
            // Dunklere Bereiche bleiben sichtbar
            data[i + 3] = 255;
          }
        }
      }
    }

    // Verarbeitete Daten zurückschreiben
    tempCtx.putImageData(imageData, 0, 0);

    return tempCanvas.toDataURL();
  },

  /**
   * Einfache S/W Konvertierung (ohne Hintergrundentfernung)
   * Erstellt DREI Versionen:
   * - Preview (400px) für Thumbnail-Anzeige
   * - Final (300px) für finale Vorschau auf Topf
   * - Engrave (2000px) für Gravur/Upload
   */
  async processSimpleBlackWhite(img) {
    const PREVIEW_SIZE = 400;  // Thumbnail
    const FINAL_SIZE = 1200;   // Finale Vorschau auf Topf - hochauflösend für bessere Qualität beim Skalieren
    const ENGRAVE_SIZE = 2000; // Upload/Gravur

    // Slider-Werte holen
    const brightness = parseInt(document.getElementById('fg-brightness')?.value || 0);
    const contrast = parseInt(document.getElementById('fg-contrast')?.value || 0);
    const threshold = parseInt(document.getElementById('fg-threshold')?.value || 127);

    // VERSION 1: Preview (400px) für Thumbnail - Reduziertes Dithering
    // Desktop: 70% Grayscale + 30% Dithered | Mobile: 85% Grayscale + 15% Dithered
    const previewImg = await this.scaleToSize(img, PREVIEW_SIZE);
    const previewDataUrl = await this.processToDitheredCanvas(
      previewImg, brightness, contrast, threshold, null, false  // false = reduziertes Dithering
    );

    // VERSION 2: Final (1200px) für Vorschau auf Topf - Reduziertes Dithering
    // Desktop: 70% Grayscale + 30% Dithered | Mobile: 85% Grayscale + 15% Dithered
    const finalImg = await this.scaleToSize(img, FINAL_SIZE);
    const finalDataUrl = await this.processToDitheredCanvas(
      finalImg, brightness, contrast, threshold, null, false  // false = reduziertes Dithering
    );

    // VERSION 3: Engrave (2000px) für Gravur - Volles Dithering (100%)
    const engraveImg = await this.scaleToSize(img, ENGRAVE_SIZE);
    const engraveDataUrl = await this.processToDitheredCanvas(
      engraveImg, brightness, contrast, threshold, null, true  // true = Dithering anwenden (für Gravur)
    );

    // Preview-Image erstellen und laden
    this.images.processedUploadPreview = new Image();
    await new Promise((resolve) => {
      this.images.processedUploadPreview.onload = () => resolve();
      this.images.processedUploadPreview.onerror = () => resolve();
      this.images.processedUploadPreview.src = previewDataUrl;
    });

    // Final-Image erstellen und laden
    this.images.processedUploadFinal = new Image();
    await new Promise((resolve) => {
      this.images.processedUploadFinal.onload = () => resolve();
      this.images.processedUploadFinal.onerror = () => resolve();
      this.images.processedUploadFinal.src = finalDataUrl;
    });

    // Engrave-Image erstellen und laden
    this.images.processedUploadEngrave = new Image();
    await new Promise((resolve) => {
      this.images.processedUploadEngrave.onload = () => resolve();
      this.images.processedUploadEngrave.onerror = () => resolve();
      this.images.processedUploadEngrave.src = engraveDataUrl;
    });

    // Legacy: Für Abwärtskompatibilität
    this.images.processedUpload = this.images.processedUploadPreview;
    // Bearbeitetes Gravur-Bild robust automatisch speichern (debounced) – unabhängig vom Submit-Hook
    this._scheduleEngraveUpload();
  },

  /**
   * Processing Spinner anzeigen/verstecken
   */
  showProcessingSpinner(show) {
    // Gelbe "Bild wird verarbeitet"-Box deaktiviert – redundant zur Optimierungs-Anzeige,
    // die ohnehin direkt danach erscheint. Box bleibt immer ausgeblendet.
    const spinner = document.getElementById('fg-processing-spinner');
    if (spinner) spinner.style.display = 'none';
  },

  // Lade-Overlay direkt über der Vorschau ("Deine Vorschau"-Modal) anzeigen
  showPreviewLoading(show, text) {
    const el = document.getElementById('fg-preview-loading');
    if (!el) return;
    if (text) {
      const t = el.querySelector('.fg-loading-text');
      if (t) t.textContent = text;
    }
    el.style.display = show ? 'flex' : 'none';
  },

  // Sichtbare Ladeanzeige direkt auf der Produktseite (waehrend KI-Optimierung beim Upload)
  showInlineLoading(show, text) {
    const el = document.getElementById('fg-inline-loading');
    if (!el) return;
    if (text) {
      const t = el.querySelector('.fg-inline-loading-text');
      if (t) t.textContent = text;
    }
    el.style.display = show ? 'block' : 'none';
    // waehrend des Ladens die alte Inline-Vorschau ausblenden
    const prev = document.getElementById('fg-inline-preview');
    if (prev && show) prev.style.display = 'none';
    // Fortschrittsbalken (asymptotisch -> Gefuehl von Fortschritt, ohne echte Restzeit)
    const bar = document.getElementById('fg-loading-bar');
    if (this._barTimer) { clearInterval(this._barTimer); this._barTimer = null; }
    if (bar) {
      if (show) {
        let p = 8;
        bar.style.width = p + '%';
        this._barTimer = setInterval(() => {
          p += (93 - p) * 0.07;            // naehert sich langsam ~93%
          bar.style.width = p.toFixed(1) + '%';
        }, 300);
      } else {
        bar.style.width = '100%';          // fertig -> voll
        setTimeout(() => { bar.style.width = '0%'; }, 500);
      }
    }
  },

  // Hinweis anzeigen, wenn die KI das Bild als Person des öffentlichen Lebens ablehnt
  showPublicFigureNotice(msg) {
    const anchor = document.getElementById('fg-inline-loading');
    let box = document.getElementById('fg-public-figure-notice');
    if (!box) {
      box = document.createElement('div');
      box.id = 'fg-public-figure-notice';
      box.style.cssText = 'display:none;margin:12px 0;padding:14px 16px;border:1px solid #e0b4b4;' +
        'background:#fff6f6;color:#9f3a38;border-radius:8px;font-size:14px;line-height:1.45;';
      if (anchor && anchor.parentNode) {
        anchor.parentNode.insertBefore(box, anchor);
      } else {
        const c = document.querySelector('#fg-preview-canvas, .fg-customizer');
        if (c && c.parentNode) c.parentNode.insertBefore(box, c);
        else document.body.appendChild(box);
      }
    }
    box.innerHTML = '⚠️ ' + (msg || 'Dieses Bild kann leider nicht für die Gravur bearbeitet werden.');
    box.style.display = 'block';
    try { box.scrollIntoView({ behavior: 'smooth', block: 'center' }); } catch (e) {}
  },

  hidePublicFigureNotice() {
    const box = document.getElementById('fg-public-figure-notice');
    if (box) box.style.display = 'none';
  },

  // Modales Popup-Hinweis (z.B. eingeschränkte Qualität bei Kind / Person des öffentlichen Lebens)
  showQualityPopup(msg) {
    let ov = document.getElementById('fg-quality-popup');
    if (!ov) {
      ov = document.createElement('div');
      ov.id = 'fg-quality-popup';
      ov.style.cssText = 'position:fixed;inset:0;z-index:99999;display:none;align-items:center;' +
        'justify-content:center;background:rgba(0,0,0,0.5);padding:20px;';
      ov.innerHTML =
        '<div style="background:#fff;max-width:430px;width:100%;border-radius:12px;padding:26px 24px;' +
        'box-shadow:0 10px 40px rgba(0,0,0,0.25);text-align:center;font-size:15px;line-height:1.55;color:#333;">' +
          '<div style="font-size:36px;margin-bottom:10px;">ℹ️</div>' +
          '<div id="fg-quality-popup-text" style="margin-bottom:20px;"></div>' +
          '<button type="button" id="fg-quality-popup-ok" style="background:#2e7d32;color:#fff;border:none;' +
          'border-radius:8px;padding:11px 26px;font-size:15px;cursor:pointer;">Verstanden</button>' +
        '</div>';
      document.body.appendChild(ov);
      const close = () => { ov.style.display = 'none'; };
      ov.addEventListener('click', (e) => { if (e.target === ov) close(); });
      const okBtn = ov.querySelector('#fg-quality-popup-ok');
      if (okBtn) okBtn.addEventListener('click', close);
    }
    const t = ov.querySelector('#fg-quality-popup-text');
    if (t) t.textContent = msg;
    ov.style.display = 'flex';
  },

  // Inline-Vorschau auf der Produktseite aktualisieren (kopiert das Topf-Mockup-Canvas in ein <img>)
  updateInlinePreview() {
    try {
      const wrap = document.getElementById('fg-inline-preview');
      const img = document.getElementById('fg-inline-preview-img');
      if (!wrap || !img || !this.canvas) return;
      if (!this.images.processedUpload && !this.uploadedImage) return;
      img.src = this.canvas.toDataURL('image/png');
      wrap.style.display = 'block';
      this.persistState(img.src);
    } catch (e) {}
  },

  _persistKey() { return 'nm_fg_' + (window.location.pathname || ''); },

  _setHidden(id, val) { const el = document.getElementById(id); if (el) el.value = val; },

  // Gravur-Zustand lokal speichern -> nach Rückkehr keine Neugenerierung (spart Gemini-Kosten).
  // WICHTIG: nur eine KLEINE Vorschau (skaliertes JPEG) speichern; das echte Gravurbild wird beim
  // Wiederkehren von der Server-URL (api/img) nachgeladen. Verhindert localStorage-Quota-Überlauf
  // (große base64-PNGs sprengten v.a. bei Dot-Art das 5-MB-Limit -> Vorschau wurde nicht mehr angezeigt).
  _smallPreview() {
    try {
      const src = this.canvas;
      if (!src || !src.width) return '';
      const maxW = 520;
      const ratio = Math.min(1, maxW / src.width);
      const sc = document.createElement('canvas');
      sc.width = Math.round(src.width * ratio);
      sc.height = Math.round(src.height * ratio);
      sc.getContext('2d').drawImage(src, 0, 0, sc.width, sc.height);
      return sc.toDataURL('image/jpeg', 0.82);
    } catch (e) { return ''; }
  },

  persistState(previewDataUrl) {
    try {
      if (!this.currentUniqueId) return;
      const small = this._smallPreview() || '';
      localStorage.setItem(this._persistKey(), JSON.stringify({
        uniqueId: this.currentUniqueId,
        preview: small,
        imageUrl: (document.getElementById('fg-hidden-image-url') || {}).value || '',
        text: (document.getElementById('fg-text') || {}).value || '',
        font: (document.getElementById('fg-font') || {}).value || '',
        bgRemoved: (document.getElementById('fg-remove-background') || {}).checked || false,
        samen: (document.getElementById('fg-samen') || {}).value || '',
        ts: Date.now()
      }));
    } catch (e) {
      // Quota überschritten -> nur Metadaten (Bild wird beim Restore von api/img geladen)
      try {
        localStorage.setItem(this._persistKey(), JSON.stringify({
          uniqueId: this.currentUniqueId,
          imageUrl: (document.getElementById('fg-hidden-image-url') || {}).value || '',
          text: (document.getElementById('fg-text') || {}).value || '',
          font: (document.getElementById('fg-font') || {}).value || '', ts: Date.now()
        }));
      } catch (e2) {}
    }
  },

  // Gespeicherten Gravur-Zustand wiederherstellen (KEIN neuer Gemini-Aufruf)
  async restoreState() {
    try {
      const raw = localStorage.getItem(this._persistKey());
      if (!raw) return;
      const d = JSON.parse(raw);
      if (!d || !d.uniqueId) return;

      this.currentUniqueId = d.uniqueId;
      this._aiSaved = true;  // verhindert Re-Upload / Neugenerierung

      // Eingabefelder wiederherstellen
      const tf = document.getElementById('fg-text'); if (tf && d.text) tf.value = d.text;
      const ff = document.getElementById('fg-font'); if (ff && d.font) ff.value = d.font;
      const bg = document.getElementById('fg-remove-background'); if (bg) bg.checked = !!d.bgRemoved;

      // Hidden-Fields für den Warenkorb
      this._setHidden('fg-hidden-image-data', d.uniqueId);
      if (d.imageUrl) this._setHidden('fg-hidden-image-url', d.imageUrl);
      this._setHidden('fg-hidden-image-uploaded', 'Ja');
      if (d.text) this._setHidden('fg-hidden-text', d.text);
      if (d.font) this._setHidden('fg-hidden-font', d.font);

      // Vorschau sofort (statisch) anzeigen
      const wrap = document.getElementById('fg-inline-preview');
      const img = document.getElementById('fg-inline-preview-img');
      if (img && d.preview) { img.src = d.preview; if (wrap) wrap.style.display = 'block'; }

      const btn = document.getElementById('fg-preview-btn'); if (btn) btn.disabled = false;

      // Echtes Gravurbild von der Server-URL nachladen (KEIN neuer Gemini-Aufruf, nur statisches Bild)
      // -> Zoom / Modal / Live-Textänderung funktionieren wieder; ersetzt die früher in localStorage
      //    abgelegten (zu großen) dataURLs.
      const imgUrl = 'https://www.workloom.de/shopify-uploads/api/img/' + encodeURIComponent(d.uniqueId) + '/';
      const imE = new Image();
      imE.crossOrigin = 'anonymous';
      imE.onload = async () => {
        try {
          let transp = imE;
          try { transp = await this.makeWhiteTransparent(imE); } catch (e) {}
          this.images.processedUploadEngrave = transp;
          this.uploadedImage = imE;
          this.images.processedUpload = transp;
          this.images.processedUploadPreview = transp;
          this.images.processedUploadFinal = transp;
          // Falls keine kleine Vorschau vorlag (z.B. alter/Quota-Stand): jetzt aufs Topf-Bild rendern
          if (!d.preview) { try { await this.renderPreview(this.getFormData()); } catch (e) {} }
        } catch (e) {}
      };
      imE.src = imgUrl;
      try { if (this.syncFieldsToCart) this.syncFieldsToCart(); } catch (e) {}
    } catch (e) {}
  },

  // Weisse Pixel transparent machen (Gravur-Vorschau: nur die Gravur bleibt sichtbar)
  makeWhiteTransparent(img, tolerance) {
    return new Promise((resolve) => {
      try {
        const t = tolerance || 235;
        const w = img.naturalWidth || img.width;
        const h = img.naturalHeight || img.height;
        const c = document.createElement('canvas');
        c.width = w; c.height = h;
        const cx = c.getContext('2d');
        cx.drawImage(img, 0, 0, w, h);
        const id = cx.getImageData(0, 0, w, h);
        const d = id.data;
        for (let i = 0; i < d.length; i += 4) {
          if (d[i] >= t && d[i + 1] >= t && d[i + 2] >= t) { d[i + 3] = 0; }
        }
        cx.putImageData(id, 0, 0);
        const out = new Image();
        out.onload = () => resolve(out);
        out.onerror = () => resolve(img);
        out.src = c.toDataURL('image/png');
      } catch (e) { resolve(img); }
    });
  },

  // Beim Upload: Bild automatisch per KI (Nano Banana Flash) fuer die Gravur optimieren
  async aiAutoOptimize() {
    if (!this.config.aiAutoOptimize || !this.currentUniqueId) { this.showInlineLoading(false); return; }
    const _mode = window.FG_AI_MODE;
    const _gen = (_mode === 'lineart' || _mode === 'dotart');
    this.showInlineLoading(true, _gen ? 'Wir zeichnen dein Bild …' : 'Wir optimieren dein Bild für die Gravur …');
    try {
      let _aiBase = this.config.aiOptimizeUrl;
      if (_mode === 'lineart') _aiBase = 'https://www.workloom.de/shopify-uploads/api/ai-lineart-public/';
      else if (_mode === 'dotart') _aiBase = 'https://www.workloom.de/shopify-uploads/api/ai-dotart-public/';
      const r = await fetch(_aiBase + encodeURIComponent(this.currentUniqueId) + '/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        // Motiv nur beim Haustier-Andenken gesetzt (window.FG_AI_MOTIV), sonst wie bisher leer
        body: window.FG_AI_MOTIV ? JSON.stringify({ motiv: window.FG_AI_MOTIV }) : '{}'
      });
      const data = await r.json();
      if (data && data.reason === 'public_figure') {
        // Gemini verweigert die Bearbeitung – Person des öffentlichen Lebens
        this._publicFigureBlocked = true;
        this._aiSaved = false;
        this.showInlineLoading(false);
        this.showPublicFigureNotice(data.error);
        // Bild verwerfen, damit es nicht (schlecht) gespeichert oder bestellt wird
        try {
          this.images.processedUploadEngrave = null;
          this.images.processedUpload = null;
          this.images.processedUploadPreview = null;
          this.images.processedUploadFinal = null;
          const prev = document.getElementById('fg-inline-preview');
          if (prev) prev.style.display = 'none';
        } catch (e) {}
        return;
      }
      if (data && data.reason === 'use_clientside') {
        // Gemini lehnt das Foto ab (z.B. Jugendschutz bei oberkörperfreiem Kind) -> Freistellung
        // lokal per body-pix erzwingen, dann S/W + Dithering. Wird anschließend normal gespeichert.
        this.showInlineLoading(true, 'Wir bereiten dein Bild für die Gravur vor …');
        try {
          if (!this.bodyPixNet && !this.bodyPixLoading) { try { await this.loadBodyPix(); } catch (e) {} }
          if (this.bodyPixNet && this._originalImg) {
            await this.processWithPersonSegmentation(this._originalImg);
          } else if (this._originalImg) {
            await this.processSimpleBlackWhite(this._originalImg);
          }
          this._aiSaved = false;  // clientseitiges Bild wird über den normalen Upload-Pfad gespeichert
          try { await this.renderPreview(this.getFormData()); } catch (e) {}
          this.showInlineLoading(false);
          this.showQualityPopup('Dein Bild wird aktuell nicht in bester Qualität angezeigt, da sich auf dem Foto ein Kind oder eine Person des öffentlichen Lebens befindet. Die automatische Optimierung ist in diesem Fall eingeschränkt – wir haben dein Foto bestmöglich für die Gravur aufbereitet.');
        } catch (e) {
          console.warn('Clientseitige Freistellung fehlgeschlagen:', e);
        } finally {
          this.showInlineLoading(false);
        }
        return;
      }
      if (data && data.success && data.image_data) {
        // ECHTES 1-Bit-Gravurbild (mit Dithering) -> nur für Zoom + Upload (= wie Workloom/Gravur)
        const pEng = new Image();
        pEng.crossOrigin = 'anonymous';
        await new Promise((res) => { pEng.onload = res; pEng.onerror = res; pEng.src = data.image_data; });
        const transpEng = await this.makeWhiteTransparent(pEng);
        this.images.processedUploadEngrave = transpEng;
        // GLATTE Graustufen-Version -> nur für die kleine Topf-Vorschau (kein Raster => kein Moiré beim Verkleinern)
        const pPrev = new Image();
        pPrev.crossOrigin = 'anonymous';
        await new Promise((res) => { pPrev.onload = res; pPrev.onerror = res; pPrev.src = data.preview_image || data.image_data; });
        const transpPrev = await this.makeWhiteTransparent(pPrev);
        this.uploadedImage = pPrev;
        this.images.processedUploadPreview = transpPrev;
        this.images.processedUploadFinal = transpPrev;
        this.images.processedUpload = transpPrev;
        this._aiSaved = true;  // Server hat das gedithene Gravur-Bild bereits gespeichert
        try { await this.renderPreview(this.getFormData()); } catch (e) {}
        try { this.persistState(); } catch (e) {}  // garantiert speichern (auch falls Render-Event ausbleibt)
      }
    } catch (e) {
      console.warn('KI-Auto-Optimierung fehlgeschlagen:', e);
    } finally {
      this.showInlineLoading(false);
    }
  },

  /**
   * Bildvorschau anzeigen
   * (Vereinfacht - Thumbnail-Container wurde entfernt)
   */
  async showImagePreview(file, img) {
    // Thumbnail-Container wurde entfernt
    // Vorschau erfolgt jetzt direkt über "Vorschau anzeigen" Button im Modal
  },

  /**
   * Manuellen Masken-Button Sichtbarkeit aktualisieren
   * (Buttons sind jetzt in Preview Modal und immer sichtbar)
   */
  updateManualMaskButtonVisibility() {
    // Nicht mehr benötigt - Buttons sind jetzt in Preview Modal
  },

  /**
   * Bild entfernen
   * (Vereinfacht - Remove-Button wurde entfernt)
   */
  removeImage() {
    const imageUpload = document.getElementById('fg-image-upload');

    if (imageUpload) {
      imageUpload.value = '';
    }

    this.uploadedImage = null;
    this.images.processedUpload = null;
    this.manualMask = null;
    this.disablePreviewButton();
  },

  /**
   * Vorschau-Button aktivieren
   */
  enablePreviewButton() {
    const btn = document.getElementById('fg-preview-btn');
    if (btn) {
      btn.disabled = false;
    }
  },

  /**
   * Vorschau-Button deaktivieren
   */
  disablePreviewButton() {
    const btn = document.getElementById('fg-preview-btn');
    if (btn) {
      btn.disabled = true;
    }
  },

  /**
   * Fonts laden
   */
  async loadFonts() {
    try {
      await document.fonts.ready;

      // Alle Schriftarten laden
      for (const [fontName, fontData] of Object.entries(this.config.fontConfig)) {
        await document.fonts.load(`48px "${fontData.name}"`);
      }

      this.fontsLoaded = true;
    } catch (error) {
      console.error('Error loading fonts:', error);
      this.fontsLoaded = true;
    }
  },

  /**
   * Base-Bild vorladen
   */
  loadBaseImage() {
    this.images.base = new Image();
    this.images.base.crossOrigin = 'anonymous';
    this.images.base.src = this.config.baseImage;
  },

  /**
   * Formulardaten sammeln
   */
  getFormData() {
    const selectedFont = document.querySelector('input[name="fg-font-choice"]:checked');
    const fontValue = selectedFont ? selectedFont.value : (document.getElementById('fg-font')?.value || 'Pixie Ring');

    return {
      text: document.getElementById('fg-text')?.value || '',
      font: fontValue,
      // Auch eine wiederhergestellte Sitzung hat ein Bild: nach der Rueckkehr
      // ist processedUpload noch leer, das Bild liegt aber schon unter
      // currentUniqueId auf dem Server. Ohne diese Ergaenzung schrieb
      // syncFieldsToCart 'Nein' in die Bestellung, obwohl eine Bild-ID danebenstand.
      hasImage: this.images.processedUpload !== null || !!this.currentUniqueId,
      removeBackground: document.getElementById('fg-remove-background')?.checked || false,
      samen: document.getElementById('fg-samen')?.value || ''
    };
  },

  /**
   * Vorschau öffnen
   */
  async openPreview() {
    if (this.isRendering) return;

    if (!this.images.processedUpload) {
      alert('Bitte lade zuerst ein Bild hoch.');
      return;
    }

    const formData = this.getFormData();
    const modal = document.getElementById('fg-preview-modal');

    if (modal) {
      modal.style.display = 'flex';
      await this.renderPreview(formData);
    }
  },

  /**
   * Vorschau schließen
   */
  closePreview() {
    const modal = document.getElementById('fg-preview-modal');
    if (modal) {
      modal.style.display = 'none';
    }
  },

  /**
   * Produkt aus Preview Modal in den Warenkorb legen
   */
  addToCartFromPreview() {
    // Modal schließen
    this.closePreview();

    // Product Form finden und submitten
    const productForm = document.querySelector('form[action*="/cart/add"]');
    if (productForm) {
      // Kurze Verzögerung damit Modal-Close-Animation läuft
      setTimeout(() => {
        productForm.requestSubmit();
      }, 100);
    } else {
      console.error('Product Form nicht gefunden');
      alert('Fehler: Warenkorb-Formular nicht gefunden. Bitte versuche es über den normalen "In den Warenkorb" Button.');
    }
  },

  /**
   * Canvas-Vorschau rendern
   */
  async renderPreview(formData) {
    if (this.isRendering) return;
    this.isRendering = true;

    try {
      // Warten auf Fonts
      if (!this.fontsLoaded) {
        await this.loadFonts();
      }

      // Canvas zurücksetzen
      this.ctx.clearRect(0, 0, this.config.canvasWidth, this.config.canvasHeight);

      // WICHTIG: Keine Glättung für scharfe Dithering-Darstellung
      this.ctx.imageSmoothingEnabled = false;

      // 1. Base-Bild zeichnen
      if (this.images.base && this.images.base.complete) {
        this.ctx.drawImage(
          this.images.base,
          0, 0,
          this.config.canvasWidth,
          this.config.canvasHeight
        );
      }

      // 2. Hochgeladenes Bild zeichnen (S/W konvertiert)
      // WICHTIG: Verwende Final-Version (300px) für 1:1 Anzeige ohne Skalierung!
      if (this.images.processedUploadFinal || this.images.processedUpload) {
        await this.drawUploadedImage();
      }

      // 3. Text zeichnen
      if (formData.text) {
        await this.drawText(formData);
      }

    } catch (error) {
      console.error('Rendering error:', error);
    } finally {
      this.isRendering = false;
      this.updateInlinePreview();
    }
  },

  /**
   * Hochgeladenes Bild zeichnen
   */
  async drawUploadedImage() {
    return new Promise((resolve) => {
      // WICHTIG: Verwende Final-Version (300px) für 1:1 Anzeige, Fallback zu processedUpload
      const img = this.images.processedUploadFinal || this.images.processedUpload;
      if (!img) {
        resolve();
        return;
      }

      const imgConfig = this.config.imageConfig;

      // Skalierung berechnen
      let width = img.width;
      let height = img.height;
      const scale = Math.min(
        imgConfig.maxWidth / width,
        imgConfig.maxHeight / height
      );

      width *= scale;
      height *= scale;

      // Bild zeichnen (zentriert) – MIT Glättung: das feine Raster (1250px) wird stark verkleinert;
      // ohne Glättung entstünde Aliasing/Moiré. Geglättet zeigt es die Tonwerte sauber (wie aus
      // normalem Betrachtungsabstand). Das echte 1-Bit-Raster sieht man per Zoom-Button / in Workloom.
      this.ctx.imageSmoothingEnabled = true;
      this.ctx.imageSmoothingQuality = 'high';
      this.ctx.drawImage(
        img,
        imgConfig.x - width / 2,
        imgConfig.y - height / 2,
        width,
        height
      );
      this.ctx.imageSmoothingEnabled = false;

      resolve();
    });
  },

  /**
   * Text zeichnen
   */
  async drawText(formData) {
    const textConfig = this.config.textConfig;
    const fontData = this.config.fontConfig[formData.font];
    const fontName = fontData ? fontData.name : 'Pixie Ring';

    // Text-Style setzen
    this.ctx.fillStyle = textConfig.color;
    this.ctx.textAlign = textConfig.align;
    this.ctx.textBaseline = textConfig.baseline;
    this.ctx.font = `${textConfig.fontSize}px "${fontName}"`;

    // Text zeichnen
    this.ctx.fillText(formData.text, textConfig.x, textConfig.y);
  },

  // Finales Gravurbild fuer den Workloom-Export: Motiv oben + Wunschtext unten eingebrannt
  async buildEngraveWithText(formData) {
    const eng = this.images.processedUploadEngrave || this.images.processedUpload;
    if (!eng) return null;
    const text = (formData.text || '').trim();
    if (!text) return eng.src;  // kein Text -> nur das Motiv exportieren
    try {
      const fontData = this.config.fontConfig[formData.font];
      const fontName = fontData ? fontData.name : 'Pixie Ring';
      try { if (document.fonts && document.fonts.ready) await document.fonts.ready; } catch (e) {}
      const w = eng.naturalWidth || eng.width || 1024;
      const h = eng.naturalHeight || eng.height || 1024;
      const textH = Math.round(h * 0.18);
      const c = document.createElement('canvas');
      c.width = w; c.height = h + textH;
      const cx = c.getContext('2d');
      cx.fillStyle = '#ffffff'; cx.fillRect(0, 0, c.width, c.height);
      cx.drawImage(eng, 0, 0, w, h);
      cx.fillStyle = '#000000';
      cx.textAlign = 'center';
      cx.textBaseline = 'middle';
      let fs = Math.round(textH * 0.55);
      cx.font = `${fs}px "${fontName}"`;
      while (cx.measureText(text).width > w * 0.9 && fs > 10) { fs -= 2; cx.font = `${fs}px "${fontName}"`; }
      cx.fillText(text, w / 2, h + textH / 2);
      return c.toDataURL('image/png');
    } catch (e) {
      console.warn('buildEngraveWithText fehlgeschlagen:', e);
      return eng.src;
    }
  },

  /**
   * Warenkorb-Integration
   */
  setupCartIntegration() {
    // Hidden Fields ins Product Form einfügen
    const productForm = document.querySelector('form[action*="/cart/add"]');

    if (productForm) {
      const hiddenFields = [
        document.getElementById('fg-hidden-text'),
        document.getElementById('fg-hidden-font'),
        document.getElementById('fg-hidden-image-uploaded'),
        document.getElementById('fg-hidden-image-data'),
        document.getElementById('fg-hidden-bg-removed'),
        document.getElementById('fg-hidden-samen')
      ];

      hiddenFields.forEach(field => {
        if (field && !productForm.contains(field)) {
          productForm.appendChild(field);
        }
      });
    }

    // Formularfelder synchronisieren
    const fields = [
      'fg-text',
      'fg-font',
      'fg-samen'
    ];

    fields.forEach(fieldId => {
      const field = document.getElementById(fieldId);
      if (field) {
        field.addEventListener('change', () => this.syncFieldsToCart());
        field.addEventListener('input', () => this.syncFieldsToCart());
        field.addEventListener('blur', () => this.syncFieldsToCart());
      }
    });

    // Schriftart-Auswahl synchronisieren
    document.querySelectorAll('input[name="fg-font-choice"]').forEach(radio => {
      radio.addEventListener('change', () => this.syncFieldsToCart());
    });

    // Checkbox synchronisieren
    const bgCheckbox = document.getElementById('fg-remove-background');
    if (bgCheckbox) {
      bgCheckbox.addEventListener('change', () => this.syncFieldsToCart());
    }

    // Bei Form Submit: Bild hochladen und dann absenden
    if (productForm) {
      const submitHandler = async (e) => {
        // Wenn bereits verarbeitet, nichts mehr tun
        if (this.uploadInProgress || this.uploadComplete) {
          return;
        }

        // IMMER preventDefault aufrufen um doppelte Submission zu verhindern
        e.preventDefault();
        e.stopImmediatePropagation();

        // Flag setzen
        this.uploadInProgress = true;

        try {
          // RACE-FIX: Klickt der Kunde „in den Warenkorb", bevor der Original-Upload fertig ist,
          // ist currentUniqueId noch leer -> kurz warten (bis ~12 s), bis die ID bereit ist.
          if (!this.currentUniqueId) {
            for (var _w = 0; _w < 60 && !this.currentUniqueId; _w++) { await new Promise(function (r) { setTimeout(r, 200); }); }
          }

          // Bild hochladen
          await this.uploadImageAndSync();

          // GARANTIE: Bild-ID immer aus der bekannten currentUniqueId ins Formular schreiben,
          // damit die Property „Bild ID" schon beim ERSTEN Hinzufügen (leerer Warenkorb) am
          // Line-Item hängt – unabhängig davon, ob der async Engrave-Upload-Block lief.
          if (this.currentUniqueId) {
            const _idf = document.getElementById('fg-hidden-image-data');
            if (_idf && !_idf.value) _idf.value = this.currentUniqueId;
            const _urf = document.getElementById('fg-hidden-image-url');
            if (_urf && !_urf.value) _urf.value = 'https://www.workloom.de/shopify-uploads/api/img/' + encodeURIComponent(this.currentUniqueId) + '/';
          }

          // Event Listener entfernen um weitere Submissions zu verhindern
          productForm.removeEventListener('submit', submitHandler);

          // Upload als komplett markieren
          this.uploadComplete = true;
          this.uploadInProgress = false;

          // Form direkt submitten (bypassed Event Listener)
          HTMLFormElement.prototype.submit.call(productForm);

        } catch (error) {
          console.error('Fehler beim Upload:', error);
          alert('Ein Fehler ist aufgetreten. Bitte versuche es erneut.');
          this.uploadInProgress = false;
        }
      };

      // Event Listener mit capture:true registrieren (wird VOR anderen Listenern ausgeführt)
      productForm.addEventListener('submit', submitHandler, { capture: true });
    }

    // Initial sync (ohne Upload)
    setTimeout(() => this.syncFieldsToCart(), 500);
  },

  /**
   * Generiere eindeutige ID für Bild-Upload
   */
  generateUniqueId() {
    const timestamp = Date.now();
    const random = Math.random().toString(36).substring(2, 9);
    return `fg_${timestamp}_${random}`;
  },

  /**
   * Bild an Django-Server hochladen
   */
  _scheduleEngraveUpload() {
    if (this._publicFigureBlocked) return;
    if (this._engraveUploadTimer) clearTimeout(this._engraveUploadTimer);
    this._engraveUploadTimer = setTimeout(async () => {
      try {
        if (this.currentUniqueId && this.images && this.images.processedUploadEngrave) {
          await this.uploadImageToServer(this.images.processedUploadEngrave.src, this.getFormData());
          console.log('✓ Bearbeitetes Gravur-Bild automatisch gespeichert');
        }
      } catch (e) {
        console.warn('Auto-Upload des bearbeiteten Bildes fehlgeschlagen:', e);
      }
    }, 1500);
  },

  async uploadImageToServer(imageData, formData, finalWithText) {
    try {
      // Verwende die unique_id vom Initial-Upload (wurde in handleImageUpload gespeichert)
      const uniqueId = this.currentUniqueId;

      if (!uniqueId) {
        console.error('❌ Keine unique_id vorhanden! Original-Bild wurde nicht hochgeladen.');
        return null;
      }

      console.log('📤 Update S/W-Bild für existing unique_id:', uniqueId);

      // Payload: Nur S/W-Bild + Metadaten (Original ist bereits hochgeladen!)
      const payload = {
        unique_id: uniqueId,
        image_data: imageData,  // Verarbeitetes S/W-Bild
        final_with_text: !!finalWithText,  // finales Bild MIT eingebranntem Wunschtext (darf KI-Bild ersetzen)
        // original_image_data wird NICHT mehr gesendet (bereits in DB)
        shopify_product_id: window.location.pathname.split('/').pop() || '',
        custom_text: formData.text,
        font_family: formData.font,
        processing_settings: {
          brightness: parseInt(document.getElementById('fg-brightness')?.value || 0),
          contrast: parseInt(document.getElementById('fg-contrast')?.value || 0),
          threshold: parseInt(document.getElementById('fg-threshold')?.value || 127),
          bg_removed: formData.removeBackground
        }
      };

      console.log('📤 S/W-Bild Upload-Payload:', {
        unique_id: uniqueId,
        has_processed_image: !!imageData,
        is_update: true
      });

      const response = await fetch(this.config.uploadUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload)
      });

      const result = await response.json();

      console.log('✅ Server-Response:', result);

      if (result.success) {
        console.log('✓ S/W-Bild erfolgreich hochgeladen (Update)');
        if (result.is_update) {
          console.log('✓ Bestätigt: Record wurde aktualisiert');
        }
        if (result.image_url) {
          console.log('✓ S/W-Bild URL:', result.image_url);
        }
        // Gib Objekt mit unique_id und image_url zurück
        return {
          unique_id: result.unique_id,
          image_url: result.image_url || null
        };
      } else {
        console.error('Fehler beim Hochladen:', result.error);
        return null;
      }
    } catch (error) {
      console.error('Upload-Fehler:', error);
      return null;
    }
  },

  /**
   * Bild für Upload vorbereiten (max 400x400px)
   */
  prepareImageForUpload(img) {
    const maxSize = 400;
    const tempCanvas = document.createElement('canvas');
    const tempCtx = tempCanvas.getContext('2d');

    // Skalierung berechnen
    let width = img.width;
    let height = img.height;

    if (width > maxSize || height > maxSize) {
      const scale = Math.min(maxSize / width, maxSize / height);
      width = Math.round(width * scale);
      height = Math.round(height * scale);
    }

    tempCanvas.width = width;
    tempCanvas.height = height;

    // Bild skaliert zeichnen
    tempCtx.drawImage(img, 0, 0, width, height);

    // Als PNG mit reduzierter Auflösung
    return tempCanvas.toDataURL('image/png');
  },

  /**
   * Bild hochladen und Felder synchronisieren (nur bei Form Submit)
   */
  async uploadImageAndSync() {
    const formData = this.getFormData();

    // Bild-ID (nur wenn Bild vorhanden)
    let uploadResult = null;
    if (this._publicFigureBlocked) {
      alert('Dieses Foto kann nicht für die Gravur verwendet werden, da es offenbar eine Person des öffentlichen Lebens zeigt. Bitte lade ein privates Foto hoch.');
      return false;
    }
    if (this.images.processedUploadEngrave || this.images.processedUpload) {
      // WICHTIG: Verwende Engrave-Version (2000px) für Upload, NICHT Preview-Version!
      // Engrave-Version ist bereits in korrekter Auflösung für Gravur
      // Finales Gravurbild = Motiv + Wunschtext unten eingebrannt (für den Workloom-Export)
      const imageData = await this.buildEngraveWithText(formData);
      uploadResult = await this.uploadImageToServer(imageData, formData, true);

      if (!uploadResult) {
        alert('Fehler: Bild konnte nicht hochgeladen werden. Bitte versuchen Sie es erneut.');
        return false;
      }

      // Bild-ID in Hidden Field setzen
      const imageDataField = document.getElementById('fg-hidden-image-data');
      if (imageDataField) {
        imageDataField.value = uploadResult.unique_id;
      }

      // Bild-URL in Hidden Field setzen (für Warenkorb-Vorschau)
      const imageUrlField = document.getElementById('fg-hidden-image-url');
      if (imageUrlField && uploadResult.image_url) {
        imageUrlField.value = uploadResult.image_url;
        console.log('✓ Bild-URL für Warenkorb gesetzt:', uploadResult.image_url);
      }
    }

    // Alle anderen Felder synchronisieren
    this.syncFieldsToCart();
    return true;
  },

  /**
   * Felder zum Warenkorb synchronisieren (ohne Upload)
   */
  syncFieldsToCart() {
    const formData = this.getFormData();

    const hiddenFields = {
      'fg-hidden-text': formData.text,
      'fg-hidden-font': formData.font,
      'fg-hidden-image-uploaded': formData.hasImage ? 'Ja' : 'Nein',
      'fg-hidden-bg-removed': formData.removeBackground ? 'Ja' : 'Nein',
      'fg-hidden-samen': formData.samen
      // Hinweis: fg-hidden-image-data wird nur bei Upload gesetzt
    };

    for (const [id, value] of Object.entries(hiddenFields)) {
      const field = document.getElementById(id);
      if (field) {
        field.value = value;
      }
    }

    // ROBUST: Bild-ID laufend mitschreiben, sobald sie bekannt ist (bei jeder Feldänderung)
    if (this.currentUniqueId) {
      const _bidf = document.getElementById('fg-hidden-image-data');
      if (_bidf && !_bidf.value) _bidf.value = this.currentUniqueId;
    }
  },

  /**
   * Schriftarten-Feld Sichtbarkeit aktualisieren
   */
  updateFontFieldVisibility() {
    const textField = document.getElementById('fg-text');
    const fontField = document.getElementById('fg-font-field');

    if (textField && fontField) {
      // Zeige Schriftarten-Feld nur wenn Text eingegeben wurde
      const hasText = textField.value.trim().length > 0;
      fontField.style.display = hasText ? 'block' : 'none';
    }
  },

  /**
   * Schriftarten-Modal öffnen
   */
  openFontModal() {
    const modal = document.getElementById('fg-font-modal');
    if (modal) {
      modal.style.display = 'flex';
    }
  },

  /**
   * Schriftarten-Modal schließen
   */
  closeFontModal() {
    const modal = document.getElementById('fg-font-modal');
    if (modal) {
      modal.style.display = 'none';
    }
  },

  /**
   * Manuelles Masking Setup
   */
  setupManualMask() {
    this.maskCanvas = document.getElementById('fg-mask-canvas');
    if (!this.maskCanvas) {
      return;
    }

    this.maskCtx = this.maskCanvas.getContext('2d');

    // Button: Manuelles Masking öffnen
    const manualMaskBtn = document.getElementById('fg-manual-mask-btn');
    if (manualMaskBtn) {
      manualMaskBtn.addEventListener('click', () => this.openManualMask());
    }

    // Button: Maske löschen
    const clearBtn = document.getElementById('fg-mask-clear');
    if (clearBtn) {
      clearBtn.addEventListener('click', () => this.clearMask());
    }

    // Button: Maske anwenden
    const applyBtn = document.getElementById('fg-mask-apply');
    if (applyBtn) {
      applyBtn.addEventListener('click', () => this.applyManualMask());
    }

    // Modal schließen
    const closeBtn = document.querySelector('.fg-mask-close');
    if (closeBtn) {
      closeBtn.addEventListener('click', () => this.closeManualMask());
    }

    // Pinselgröße
    const brushSizeSlider = document.getElementById('fg-brush-size');
    const brushSizeDisplay = document.getElementById('fg-brush-size-display');
    if (brushSizeSlider && brushSizeDisplay) {
      brushSizeSlider.addEventListener('input', (e) => {
        this.brushSize = parseInt(e.target.value);
        brushSizeDisplay.textContent = this.brushSize;
      });
    }

    // Canvas Zeichnen
    this.maskCanvas.addEventListener('mousedown', (e) => this.startDrawing(e));
    this.maskCanvas.addEventListener('mousemove', (e) => this.draw(e));
    this.maskCanvas.addEventListener('mouseup', () => this.stopDrawing());
    this.maskCanvas.addEventListener('mouseleave', () => this.stopDrawing());

    // Touch Support
    this.maskCanvas.addEventListener('touchstart', (e) => {
      e.preventDefault();
      const touch = e.touches[0];
      const mouseEvent = new MouseEvent('mousedown', {
        clientX: touch.clientX,
        clientY: touch.clientY
      });
      this.maskCanvas.dispatchEvent(mouseEvent);
    });

    this.maskCanvas.addEventListener('touchmove', (e) => {
      e.preventDefault();
      const touch = e.touches[0];
      const mouseEvent = new MouseEvent('mousemove', {
        clientX: touch.clientX,
        clientY: touch.clientY
      });
      this.maskCanvas.dispatchEvent(mouseEvent);
    });

    this.maskCanvas.addEventListener('touchend', (e) => {
      e.preventDefault();
      const mouseEvent = new MouseEvent('mouseup', {});
      this.maskCanvas.dispatchEvent(mouseEvent);
    });
  },

  /**
   * Manuelles Masking Modal öffnen
   */
  openManualMask() {
    if (!this.uploadedImage) {
      alert('Bitte lade zuerst ein Bild hoch.');
      return;
    }

    const modal = document.getElementById('fg-manual-mask-modal');
    if (!modal) return;

    // Canvas vorbereiten
    this.maskCanvas.width = this.uploadedImage.width;
    this.maskCanvas.height = this.uploadedImage.height;

    // Originalbild zeichnen
    this.maskCtx.clearRect(0, 0, this.maskCanvas.width, this.maskCanvas.height);
    this.maskCtx.drawImage(this.uploadedImage, 0, 0);

    // Original-Bilddaten speichern für späteren Vergleich
    this.originalMaskImageData = this.maskCtx.getImageData(0, 0, this.maskCanvas.width, this.maskCanvas.height);

    // Wenn bereits eine Maske existiert, anzeigen
    if (this.manualMask) {
      this.drawExistingMask();
    }

    modal.style.display = 'flex';
  },

  /**
   * Existierende Maske zeichnen
   */
  drawExistingMask() {
    const imageData = this.maskCtx.getImageData(0, 0, this.maskCanvas.width, this.maskCanvas.height);
    const data = imageData.data;

    for (let i = 0; i < this.manualMask.length; i++) {
      if (this.manualMask[i]) {
        const pixelIndex = i * 4;
        // Grüner Overlay für markierte Bereiche
        data[pixelIndex + 1] = Math.min(255, data[pixelIndex + 1] + 150);  // Starke Grün-Erhöhung
      }
    }

    this.maskCtx.putImageData(imageData, 0, 0);
  },

  /**
   * Manuelles Masking Modal schließen
   */
  closeManualMask() {
    const modal = document.getElementById('fg-manual-mask-modal');
    if (modal) {
      modal.style.display = 'none';
    }
  },

  /**
   * Zeichnen starten
   */
  startDrawing(e) {
    this.isDrawing = true;
    this.draw(e);
  },

  /**
   * Zeichnen stoppen
   */
  stopDrawing() {
    this.isDrawing = false;
  },

  /**
   * Zeichnen
   */
  draw(e) {
    if (!this.isDrawing) return;

    const rect = this.maskCanvas.getBoundingClientRect();
    const scaleX = this.maskCanvas.width / rect.width;
    const scaleY = this.maskCanvas.height / rect.height;

    const x = (e.clientX - rect.left) * scaleX;
    const y = (e.clientY - rect.top) * scaleY;

    // Grüner Kreis für Pinsel - höhere Deckkraft für bessere Erkennung
    this.maskCtx.fillStyle = 'rgba(0, 255, 0, 0.7)';
    this.maskCtx.beginPath();
    this.maskCtx.arc(x, y, this.brushSize, 0, Math.PI * 2);
    this.maskCtx.fill();
  },

  /**
   * Maske löschen
   */
  clearMask() {
    this.maskCtx.clearRect(0, 0, this.maskCanvas.width, this.maskCanvas.height);
    this.maskCtx.drawImage(this.uploadedImage, 0, 0);
    this.manualMask = null;
  },

  /**
   * Manuelle Maske anwenden
   */
  async applyManualMask() {
    try {
      // Aktuelle Canvas-Daten holen (mit grünen Markierungen)
      const currentImageData = this.maskCtx.getImageData(0, 0, this.maskCanvas.width, this.maskCanvas.height);
      const currentData = currentImageData.data;
      const originalData = this.originalMaskImageData.data;

      // Boolean Array für Maske erstellen
      this.manualMask = new Array(this.maskCanvas.width * this.maskCanvas.height);

      let markedPixels = 0;
      for (let i = 0; i < this.manualMask.length; i++) {
        const pixelIndex = i * 4;

        // Vergleiche aktuellen Grün-Wert mit Original
        const currentG = currentData[pixelIndex + 1];
        const originalG = originalData[pixelIndex + 1];

        // Wenn Grün-Kanal deutlich höher ist als im Original → wurde gemalt
        const greenIncrease = currentG - originalG;
        this.manualMask[i] = greenIncrease > 50;  // Mindestens 50 Grün-Erhöhung

        if (this.manualMask[i]) markedPixels++;
      }

      console.log(`✓ Manuelle Maske erstellt: ${markedPixels} markierte Pixel von ${this.manualMask.length} gesamt (${(markedPixels / this.manualMask.length * 100).toFixed(1)}%)`);

      // Modal sofort schließen
      this.closeManualMask();

      // Spinner im Hauptbereich anzeigen
      this.showProcessingSpinner(true);

      // Kurze Pause damit Modal-Animation abgeschlossen ist
      await new Promise(resolve => setTimeout(resolve, 100));

      // Bild mit manueller Maske verarbeiten
      await this.processWithManualMask();

      // Wenn Preview Modal offen ist, Vorschau aktualisieren
      const modal = document.getElementById('fg-preview-modal');
      if (modal && modal.style.display === 'flex') {
        await this.renderPreview(this.getFormData());
      }
    } catch (error) {
      console.error('Fehler beim Anwenden der Maske:', error);
    } finally {
      this.showProcessingSpinner(false);
    }
  },

  /**
   * Bild mit manueller Maske verarbeiten
   * Erstellt ZWEI Versionen:
   * - Preview (500px) für Thumbnail-Anzeige
   * - Engrave (2000px) für Gravur/Upload
   * HINWEIS: Manuelle Maske basiert auf Original-Größe, wird für beide Versionen skaliert
   */
  async processWithManualMask() {
    const PREVIEW_SIZE = 400;  // Thumbnail
    const FINAL_SIZE = 1200;   // Finale Vorschau auf Topf - hochauflösend für bessere Qualität beim Skalieren
    const ENGRAVE_SIZE = 2000; // Upload/Gravur

    const brightness = parseInt(document.getElementById('fg-brightness')?.value || 0);
    const contrast = parseInt(document.getElementById('fg-contrast')?.value || 0);
    const threshold = parseInt(document.getElementById('fg-threshold')?.value || 127);

    // Original-Bildgröße
    const origWidth = this.uploadedImage.width;
    const origHeight = this.uploadedImage.height;

    // Hilfsfunktion: Maske auf neue Größe skalieren
    const scaleMask = (mask, origW, origH, newW, newH) => {
      const newMask = new Array(newW * newH);
      const scaleX = origW / newW;
      const scaleY = origH / newH;

      for (let y = 0; y < newH; y++) {
        for (let x = 0; x < newW; x++) {
          const origX = Math.floor(x * scaleX);
          const origY = Math.floor(y * scaleY);
          const origIdx = origY * origW + origX;
          newMask[y * newW + x] = mask[origIdx];
        }
      }
      return newMask;
    };

    // VERSION 1: Preview (400px) - OHNE Dithering (Grayscale)
    const previewImg = await this.scaleToSize(this.uploadedImage, PREVIEW_SIZE);
    const previewMask = scaleMask(this.manualMask, origWidth, origHeight, previewImg.width, previewImg.height);
    const previewDataUrl = await this.processToDitheredCanvas(
      previewImg, brightness, contrast, threshold, previewMask, false
    );

    // VERSION 2: Final (1200px) - OHNE Dithering (Grayscale)
    const finalImg = await this.scaleToSize(this.uploadedImage, FINAL_SIZE);
    const finalMask = scaleMask(this.manualMask, origWidth, origHeight, finalImg.width, finalImg.height);
    const finalDataUrl = await this.processToDitheredCanvas(
      finalImg, brightness, contrast, threshold, finalMask, false
    );

    // VERSION 3: Engrave (2000px) - MIT Dithering (für Gravur)
    const engraveImg = await this.scaleToSize(this.uploadedImage, ENGRAVE_SIZE);
    const engraveMask = scaleMask(this.manualMask, origWidth, origHeight, engraveImg.width, engraveImg.height);
    const engraveDataUrl = await this.processToDitheredCanvas(
      engraveImg, brightness, contrast, threshold, engraveMask, true
    );

    // Preview-Image erstellen und laden
    this.images.processedUploadPreview = new Image();
    await new Promise((resolve) => {
      this.images.processedUploadPreview.onload = () => resolve();
      this.images.processedUploadPreview.onerror = () => resolve();
      this.images.processedUploadPreview.src = previewDataUrl;
    });

    // Final-Image erstellen und laden
    this.images.processedUploadFinal = new Image();
    await new Promise((resolve) => {
      this.images.processedUploadFinal.onload = () => resolve();
      this.images.processedUploadFinal.onerror = () => resolve();
      this.images.processedUploadFinal.src = finalDataUrl;
    });

    // Engrave-Image erstellen und laden
    this.images.processedUploadEngrave = new Image();
    await new Promise((resolve) => {
      this.images.processedUploadEngrave.onload = () => resolve();
      this.images.processedUploadEngrave.onerror = () => resolve();
      this.images.processedUploadEngrave.src = engraveDataUrl;
    });

    // Legacy: Für Abwärtskompatibilität
    this.images.processedUpload = this.images.processedUploadPreview;
    // Bearbeitetes Gravur-Bild robust automatisch speichern (debounced) – unabhängig vom Submit-Hook
    this._scheduleEngraveUpload();
  }
};

// Auto-Initialisierung
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => FotogravurPreview.init());
} else {
  FotogravurPreview.init();
}
